import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { ReceiptPDFA4, ReceiptProps } from '../components/ReceiptPDFA4';
import { BusinessUnit } from '../../common/enums';
import { IMAGE_TYPES, PAPER_SIZE } from '../../types/enums';
import { ImageExtension } from '../../badge/extensions/image/ImageExtension';
import { ZPL } from '../../badge/zpl/ZPL';
import { ZplOptions } from '../../types/badge';

export interface ReceiptBuilderProps extends Omit<ReceiptProps, 'size'> {
  businessUnit: BusinessUnit;
  zplOptions?: ZplOptions;
}

class ReceiptBuilder {
  private static getSize(businessUnit: BusinessUnit) {
    if (businessUnit === BusinessUnit.US) {
      return PAPER_SIZE.FOUR_X_TEN_INCH;
    }
    if (businessUnit === BusinessUnit.AUSTRALIA) {
      return PAPER_SIZE.W105xH140mm;
    }
    return PAPER_SIZE.A4;
  }

  static async getZplReceipt(blob: Blob, zplOptions: ReceiptBuilderProps['zplOptions']) {
    const { image } = await ImageExtension.getImageFromPDF(blob, IMAGE_TYPES.PNG, zplOptions);
    return ZPL.getZPL64FromImage(image, { black: 60, notrim: true });
  }

  static async getBlob({
    businessUnit,
    zplOptions = {
      width: 640 + 318,
      height: 1180,
      scale: 2.9
    },
    ...props
  }: ReceiptBuilderProps) {
    const size = this.getSize(businessUnit);
    try {
      const blob = await pdf(<ReceiptPDFA4 {...props} size={size} />).toBlob();
      if (!blob) {
        throw new Error('pdf().toBlob() returned undefined');
      }
      if (businessUnit === BusinessUnit.AUSTRALIA) {
        const zpl = await this.getZplReceipt(blob, zplOptions);
        if (!zpl) {
          throw new Error('ZPL conversion returned undefined');
        }
        return zpl;
      }
      return blob;
    } catch (e) {
      console.error('Error generating receipt blob', e);
      throw e;
    }
  }
}

export default ReceiptBuilder;
